import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Eye, ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { ProviderBooking } from "@/hooks/useProviderBookings";

interface BookingsTableProps {
  bookings: ProviderBooking[];
  title: string;
  onViewDetails: (bookingId: string) => void;
  onStatusUpdate: (bookingId: string, status: string) => void;
}

const ITEMS_PER_PAGE = 10;

const BookingsTable = ({ bookings, title, onViewDetails, onStatusUpdate }: BookingsTableProps) => {
  const [currentPage, setCurrentPage] = useState(1);

  // FIXED: Format time without timezone conversion to prevent offset issues
  const formatTime = (timeString: string) => {
    try {
      // Parse the time string directly without creating a Date object to avoid timezone issues
      const [hours, minutes] = timeString.split(':');
      const hourNum = parseInt(hours, 10);
      const minuteNum = parseInt(minutes, 10);
      
      // Format as HH:MM (24-hour format for provider dashboard)
      return `${hourNum.toString().padStart(2, '0')}:${minuteNum.toString().padStart(2, '0')}`;
    } catch (error) {
      console.error('Error formatting time:', timeString, error);
      return timeString; // Return original if parsing fails
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch (error) {
      return dateString;
    }
  };

  const getStatusColor = (status: string) => {
    const normalizedStatus = status.toLowerCase();
    switch (normalizedStatus) {
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in session':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Function to determine the actual status including "in session" logic
  const getActualStatus = (booking: ProviderBooking) => {
    const currentTime = new Date();
    const isCancelled = booking.status.toLowerCase() === 'cancelled';
    const statusIsCompleted = booking.status.toLowerCase() === 'completed';
    
    // Create proper date objects for comparison
    const [startHours, startMinutes] = booking.start_time.split(':').map(Number);
    const [endHours, endMinutes] = booking.end_time.split(':').map(Number);
    
    const bookingDateTime = new Date(booking.booking_date);
    bookingDateTime.setHours(startHours, startMinutes, 0, 0);
    
    const sessionEnd = new Date(booking.booking_date);
    sessionEnd.setHours(endHours, endMinutes, 0, 0);

    // Don't mark as completed just because time has passed - only if actually completed in database
    const isCompleted = statusIsCompleted;

    // UPDATED: Check for actual check-in status using database fields first
    const hasCheckedInFromDatabase = booking.checked_in_at && !booking.checked_out_at;
    
    // Check for notes-based check-in as fallback (same logic as BookingCard)
    const isCheckedInFromNotes = !isCompleted && !isCancelled && 
      (booking.notes?.includes('[CHECKED IN') || booking.notes?.toLowerCase().includes('checked in'));
    
    // If checked in (either database or notes) and not completed/cancelled, show "In Session"
    if ((hasCheckedInFromDatabase || isCheckedInFromNotes) && !isCompleted && !isCancelled) {
      return 'In Session';
    }

    // Otherwise return the original status
    return booking.status;
  };

  if (bookings.length === 0) return null;

  // Calculate pagination
  const totalPages = Math.ceil(bookings.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentBookings = bookings.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        items.push(
          <PaginationItem key={i}>
            <PaginationLink
              isActive={currentPage === i}
              onClick={() => goToPage(i)}
              className="cursor-pointer"
            >
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }
    } else {
      // Show ellipsis for large page counts
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          items.push(
            <PaginationItem key={i}>
              <PaginationLink
                isActive={currentPage === i}
                onClick={() => goToPage(i)}
                className="cursor-pointer"
              >
                {i}
              </PaginationLink>
            </PaginationItem>
          );
        }
        items.push(<PaginationEllipsis key="ellipsis-end" />);
        items.push(
          <PaginationItem key={totalPages}>
            <PaginationLink
              onClick={() => goToPage(totalPages)}
              className="cursor-pointer"
            >
              {totalPages}
            </PaginationLink>
          </PaginationItem>
        );
      } else if (currentPage >= totalPages - 2) {
        items.push(
          <PaginationItem key={1}>
            <PaginationLink
              onClick={() => goToPage(1)}
              className="cursor-pointer"
            >
              1
            </PaginationLink>
          </PaginationItem>
        );
        items.push(<PaginationEllipsis key="ellipsis-start" />);
        for (let i = totalPages - 3; i <= totalPages; i++) {
          items.push(
            <PaginationItem key={i}>
              <PaginationLink
                isActive={currentPage === i}
                onClick={() => goToPage(i)}
                className="cursor-pointer"
              >
                {i}
              </PaginationLink>
            </PaginationItem>
          );
        }
      } else {
        items.push(
          <PaginationItem key={1}>
            <PaginationLink
              onClick={() => goToPage(1)}
              className="cursor-pointer"
            >
              1
            </PaginationLink>
          </PaginationItem>
        );
        items.push(<PaginationEllipsis key="ellipsis-start" />);
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          items.push(
            <PaginationItem key={i}>
              <PaginationLink
                isActive={currentPage === i}
                onClick={() => goToPage(i)}
                className="cursor-pointer"
              >
                {i}
              </PaginationLink>
            </PaginationItem>
          );
        }
        items.push(<PaginationEllipsis key="ellipsis-end" />);
        items.push(
          <PaginationItem key={totalPages}>
            <PaginationLink
              onClick={() => goToPage(totalPages)}
              className="cursor-pointer"
            >
              {totalPages}
            </PaginationLink>
          </PaginationItem>
        );
      }
    }
    
    return items;
  };

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <Badge variant="secondary" className="ml-2">{bookings.length}</Badge>
        </div>
        {totalPages > 1 && (
          <div className="text-sm text-gray-600">
            Showing {startIndex + 1}-{Math.min(endIndex, bookings.length)} of {bookings.length}
          </div>
        )}
      </div>
      
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-32">Booking #</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Service</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Time</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-32">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentBookings.map((booking) => {
              const actualStatus = getActualStatus(booking);
              
              return (
                <TableRow key={booking.id} className="hover:bg-gray-50">
                  <TableCell className="font-medium">
                    <button
                      onClick={() => onViewDetails(booking.id)}
                      className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                    >
                      #{booking.booking_number}
                    </button>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{booking.customer_name}</div>
                      {booking.customer_phone && (
                        <div className="text-sm text-gray-500">{booking.customer_phone}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{booking.service_name}</TableCell>
                  <TableCell>{formatDate(booking.booking_date)}</TableCell>
                  <TableCell>{formatTime(booking.start_time)} - {formatTime(booking.end_time)}</TableCell>
                  <TableCell>{booking.currency} {booking.price.toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge className={`${getStatusColor(actualStatus)} pointer-events-none`}>
                      {actualStatus}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onViewDetails(booking.id)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      
                      {booking.status.toLowerCase() === 'pending' && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm">
                              Update
                              <ChevronDown className="h-4 w-4 ml-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => onStatusUpdate(booking.id, 'confirmed')}>
                              Confirm
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onStatusUpdate(booking.id, 'cancelled')}>
                              Cancel
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      
                      {booking.status.toLowerCase() === 'confirmed' && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm">
                              Update
                              <ChevronDown className="h-4 w-4 ml-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => onStatusUpdate(booking.id, 'completed')}>
                              Mark Complete
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onStatusUpdate(booking.id, 'cancelled')}>
                              Cancel
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center mt-4">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => currentPage > 1 && goToPage(currentPage - 1)}
                  className={`cursor-pointer ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                />
              </PaginationItem>
              
              {renderPaginationItems()}
              
              <PaginationItem>
                <PaginationNext
                  onClick={() => currentPage < totalPages && goToPage(currentPage + 1)}
                  className={`cursor-pointer ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default BookingsTable;
